import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
import { devicePurposes, type DevicePurpose } from './device-purpose';

/** 서버가 Unit에 연결된 LoRa 도어락 하나를 실시간으로 읽은 결과. PIN 원문은 어떤 경우에도 오지 않는다. */
export type PinLookup =
  | 'OBSERVED'
  | 'ABSENT'
  | 'MISMATCH'
  | 'UNAVAILABLE'
  | 'NOT_CONFIGURED'
  | 'NO_RESERVATION';
export type PinState = 'set' | 'trying to set' | 'unset';
export type PinReservationSource = 'CORE_RESERVATION' | 'JAVA_SLOT';

export interface AccessPinStatusItem {
  core_device_id: string;
  device_id: string;
  gateway_id: string | null;
  purpose: DevicePurpose;
  reservation_source: PinReservationSource | null;
  lookup: PinLookup;
  pin_configured: boolean | null;
  pin_state: PinState | null;
  /** 서버가 준 핀 모드 원문. 값의 의미는 해석하지 않고 그대로 보여 준다. */
  pin_mode: string | null;
  /** `multi-pin`이면 true. 그 밖은 null — false가 아니라 '확인되지 않음'이다. */
  multi_pin: true | null;
}
export interface AccessPinStatus {
  unit_id: string;
  observed_at: string;
  items: AccessPinStatusItem[];
}

/** 서버 계약: 연결 기기 50대 초과는 503이므로 응답은 50건을 넘지 않는다. */
const MAX_ITEMS = 50;
const LOOKUPS: readonly string[] = [
  'OBSERVED',
  'ABSENT',
  'MISMATCH',
  'UNAVAILABLE',
  'NOT_CONFIGURED',
  'NO_RESERVATION',
];
const itemKeys = [
  'core_device_id',
  'device_id',
  'gateway_id',
  'purpose',
  'reservation_source',
  'lookup',
  'pin_configured',
  'pin_state',
  'pin_mode',
  'multi_pin',
];
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const nullableString = (value: unknown) => value === null || typeof value === 'string';
const nullableBoolean = (value: unknown) => value === null || typeof value === 'boolean';
/** 모르는 키는 거절한다 — 계약에 없는 값(특히 PIN 원문)이 조용히 화면까지 오지 않게. */
const hasOnly = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);

function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: 'PIN 설정 상태 응답을 확인하지 못했습니다.',
  });
}

function parseItem(value: unknown): AccessPinStatusItem {
  if (
    !record(value) ||
    !hasOnly(value, itemKeys) ||
    typeof value.core_device_id !== 'string' ||
    !isUuid(value.core_device_id) ||
    typeof value.device_id !== 'string' ||
    !nullableString(value.gateway_id) ||
    typeof value.purpose !== 'string' ||
    !Object.hasOwn(devicePurposes, value.purpose) ||
    !(
      value.reservation_source === null ||
      value.reservation_source === 'CORE_RESERVATION' ||
      value.reservation_source === 'JAVA_SLOT'
    ) ||
    typeof value.lookup !== 'string' ||
    !LOOKUPS.includes(value.lookup) ||
    !nullableBoolean(value.pin_configured) ||
    !(
      value.pin_state === null ||
      value.pin_state === 'set' ||
      value.pin_state === 'trying to set' ||
      value.pin_state === 'unset'
    ) ||
    !nullableString(value.pin_mode) ||
    !(value.multi_pin === null || value.multi_pin === true)
  )
    throw invalid();
  return value as unknown as AccessPinStatusItem;
}

/**
 * 슬롯(Unit)에 연결된 LoRa 도어락의 PIN 설정 여부·멀티핀 여부를 서버가 장비에서 실시간으로 읽은 결과.
 * 서버는 응답 시간 제한 없이 LoRa 응답을 기다리고, 사용자 결정에 따라 이 호출만 클라이언트 제한 시간을
 * 명시적으로 끈다(`timeout: false`). 서비스 기본 15초와 다른 호출은 그대로이며, 화면을 떠나거나 취소하면
 * signal로 끊긴다. 읽기 전용이며 어떤 값도 쓰지 않는다.
 */
export async function readAccessPinStatus(
  client: ServiceClient,
  unitId: string,
  signal?: AbortSignal,
): Promise<AccessPinStatus> {
  if (!isUuid(unitId)) throw invalid();
  const value = await client.get<unknown>(`admin/units/${unitId}/access-pin-status`, {
    signal,
    cache: 'no-store',
    timeout: false,
  });
  if (
    !record(value) ||
    !hasOnly(value, ['unit_id', 'observed_at', 'items']) ||
    typeof value.unit_id !== 'string' ||
    value.unit_id.toLowerCase() !== unitId.toLowerCase() ||
    !isOffsetDateTime(value.observed_at) ||
    !Array.isArray(value.items) ||
    value.items.length > MAX_ITEMS
  )
    throw invalid();
  const items = value.items.map(parseItem);
  if (new Set(items.map((item) => item.core_device_id)).size !== items.length) throw invalid();
  return { unit_id: value.unit_id, observed_at: value.observed_at, items };
}
